import { useMutation } from "@apollo/client";
import { ChangeEvent } from "react";
import {
  GetMeetingTranscriptQueryQuery,
  MeetingBotJoinReason,
  MeetingBotSetting,
} from "types/graphql-schema";
import { MeetingViewMeetingNodeNewPageFragmentFragment } from "types/graphql-schema";

import { successNotificationVar } from "@cache/cache";
import InfoTooltip from "@components/tooltip/info-tooltip";
import { classNames } from "@helpers/css";

import getMeetingQuery from "../graphql/get-meeting-query";
import updateMeetingGroupMutation from "../graphql/update-meeting-group-mutation";

const MeetingNotetakerCheckbox = ({
  meeting,
  className,
  label = "Automatically join",
}: {
  meeting:
    | MeetingViewMeetingNodeNewPageFragmentFragment
    | NonNullable<GetMeetingTranscriptQueryQuery["meeting"]>;
  className?: string;
  label?: string;
}) => {
  const [updateMeetingGroup] = useMutation(updateMeetingGroupMutation, {
    refetchQueries: [getMeetingQuery],
  });

  const handleToggleNotetakerAutomaticJoin = (
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    if (!meeting.meetingGroup) return;
    const meetingBotSetting = e.target.checked
      ? MeetingBotSetting.AlwaysJoin
      : MeetingBotSetting.NeverJoin;
    updateMeetingGroup({
      variables: {
        meetingGroupId: meeting.meetingGroup.id,
        meetingBotSetting,
      },
      optimisticResponse: {
        updateMeetingGroup: {
          meetingGroup: {
            ...meeting.meetingGroup,
            meetingBotSetting,
          },
        },
      },

      onCompleted: () => {
        successNotificationVar({
          title: "Notetaker settings updated",
        });
      },
    });
  };

  const meetingGroup = meeting.meetingGroup;

  if (!meetingGroup) return null;

  const joinBecauseOfParticipants =
    meeting.botParticipationDetails.joinBecauseOfParticipants
      .slice(0, 2)
      .map((participant) => participant.name)
      .join(", ");
  const notetakerTooltip = `${
    meeting.botParticipationDetails.reason ===
    MeetingBotJoinReason.MeetingFlagEnabled
      ? "The notetaker has been enabled for this meeting."
      : meeting.botParticipationDetails.reason ===
          MeetingBotJoinReason.MeetingFlagDisabled
        ? "The notetaker has been disabled for this meeting."
        : meeting.botParticipationDetails.reason ===
            MeetingBotJoinReason.NoParticipantsWithSettingEnabled
          ? "None of the meeting participants have the notetaker enabled in their user settings."
          : meeting.botParticipationDetails.reason ===
              MeetingBotJoinReason.OrgSettingDisabled
            ? "The notetaker has been disabled by the organization."
            : meeting.botParticipationDetails.reason ===
                MeetingBotJoinReason.ParticipantSettingEnabled
              ? `The notetaker is enabled by the user settings of ${joinBecauseOfParticipants}`
              : ""
  }<br /><br />${
    meeting.botParticipationDetails.willBotJoin
      ? "The notetaker will automatically request to join the meeting to take notes."
      : "The notetaker will not join the meeting and will not take notes."
  }`;

  return (
    <label className={classNames(className, "cursor-pointer")}>
      <span className="w-3 flex justify-center items-center">
        <input
          type="checkbox"
          checked={
            meeting.meetingGroup.meetingBotSetting ===
              MeetingBotSetting.AlwaysJoin ||
            (meeting.meetingGroup.meetingBotSetting ===
              MeetingBotSetting.UseParticipantsSetting &&
              meeting.botParticipationDetails.willBotJoin)
          }
          onChange={handleToggleNotetakerAutomaticJoin}
          disabled={
            !meeting.meetingGroup.canUpdate?.permission ||
            (!!meeting.organization &&
              meeting.organization.enableMeetingBot === false)
          }
        />
      </span>
      <span>{label}</span>

      <InfoTooltip text={notetakerTooltip} />
    </label>
  );
};

export default MeetingNotetakerCheckbox;
